import {
  Box,
  FormControl,
  FormLabel,
  Input,
  useToast
} from "@chakra-ui/react";
import {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { useHistory, useParams } from "react-router-dom";

import { useMissionsApi } from "src/api/missions/missions.api";
import { SolidButton } from "src/components/button";
import { DatePicker } from "src/components/pickers";
import { Header } from "src/components/shared/Header";
import { Text } from "src/components/text";
import { theme } from "src/components/theme";
import {
  eachUtcDayKey,
  formatUtcDate,
  missionDayNumber,
  pickerDateToUtcKey,
  utcKeyToPickerDate
} from "src/utils/utcDate";

interface PageParams {
  missionId?: string;
}

interface DayRow {
  date: string;
  minutes: number;
  seconds: number;
}

const rowSeconds = (row: DayRow) => (row.minutes * 60) + row.seconds;

const splitSeconds = (total: number): Pick<DayRow, "minutes" | "seconds"> => {
  const safe = Math.max(0, Math.floor(total));

  return {
    minutes: Math.floor(safe / 60),
    seconds: safe % 60
  };
};

const defaultRow = (date: string): DayRow => ({
  date,
  minutes: 0,
  seconds: 10
});

export const MissionFormPage: React.FC = () => {
  const { missionId } = useParams<PageParams>();
  const isEdit = Boolean(missionId);
  const history = useHistory();
  const toast = useToast();
  const [ missionRes, missionReq ] = useMissionsApi("GET_MISSION");
  const [ createRes, createReq ] = useMissionsApi("CREATE_MISSION");
  const [ updateRes, updateReq ] = useMissionsApi("UPDATE_MISSION");
  const [ step, setStep ] = useState<1 | 2>(1);
  const [ name, setName ] = useState("");
  const [ startDate, setStartDate ] = useState<Date | null>(null);
  const [ endDate, setEndDate ] = useState<Date | null>(null);
  const [ isGeneral, setIsGeneral ] = useState(false);
  const [ rows, setRows ] = useState<DayRow[]>([]);
  const [ bulkFrom, setBulkFrom ] = useState("");
  const [ bulkTo, setBulkTo ] = useState("");
  const [ bulkMinutes, setBulkMinutes ] = useState(0);
  const [ bulkSeconds, setBulkSeconds ] = useState(10);
  const [ incrementSeconds, setIncrementSeconds ] = useState(10);
  const [ loaded, setLoaded ] = useState(!isEdit);

  useEffect(() => {
    if (missionId) {
      missionReq({ id: missionId });
    }
  }, [ missionId, missionReq ]);

  useEffect(() => {
    if (!missionRes.data || loaded) {
      return;
    }

    const mission = missionRes.data;

    setName(mission.name);
    setStartDate(utcKeyToPickerDate(mission.startDate));
    setEndDate(mission.endDate ? utcKeyToPickerDate(mission.endDate) : null);
    setIsGeneral(mission.isGeneral);

    setRows(mission.days.map(day => ({
      date: day.date.slice(0, 10),
      minutes: Math.floor(day.latencySeconds / 60),
      seconds: day.latencySeconds % 60
    })));
    setLoaded(true);
  }, [ missionRes.data, loaded ]);

  useEffect(() => {
    if (missionRes.error) {
      toast({
        status: "error",
        title: "Could not load mission",
        description: missionRes.error.details
      });
    }
  }, [ missionRes.error, toast ]);

  useEffect(() => {
    const saved = createRes.data || updateRes.data;

    if (saved) {
      toast({
        status: "success",
        title: isEdit ? "Mission updated" : "Mission created"
      });
      history.push("/missions");
    }
  }, [
    createRes.data,
    updateRes.data,
    history,
    isEdit,
    toast
  ]);

  useEffect(() => {
    const error = createRes.error || updateRes.error;

    if (error) {
      toast({
        status: "error",
        title: "Could not save mission",
        description: error.details
      });
    }
  }, [
    createRes.error,
    updateRes.error,
    toast
  ]);

  const openEnded = isGeneral && !endDate;
  const startKey = startDate ? pickerDateToUtcKey(startDate) : "";
  const endKey = endDate ? pickerDateToUtcKey(endDate) : "";

  const goToDays = () => {
    if (!name.trim()) {
      toast({
        status: "error",
        title: "Enter a mission name"
      });

      return;
    }

    if (!startDate) {
      toast({
        status: "error",
        title: "Enter a start date"
      });

      return;
    }

    if (!isGeneral && !endDate) {
      toast({
        status: "error",
        title: "Enter an end date"
      });

      return;
    }

    if (endDate && pickerDateToUtcKey(endDate) < pickerDateToUtcKey(startDate)) {
      toast({
        status: "error",
        title: "End date must be on or after the start date"
      });

      return;
    }

    if (endDate) {
      const existing = new Map(rows.map(row => [ row.date, row ]));

      setRows(eachUtcDayKey(pickerDateToUtcKey(startDate), pickerDateToUtcKey(endDate)).map(date => existing.get(date) || defaultRow(date)));
    } else {
      setRows(current => current.filter(row => rowSeconds(row) !== 10));
    }

    setStep(2);
  };

  const rangeReady = () => {
    if (!bulkFrom || !bulkTo || bulkFrom > bulkTo) {
      toast({
        status: "error",
        title: "Choose a start and end day for the range"
      });

      return false;
    }

    if (bulkMinutes < 0 || bulkSeconds < 0 || bulkSeconds > 59) {
      toast({
        status: "error",
        title: "Enter minutes of 0 or more, and seconds from 0 to 59"
      });

      return false;
    }

    return true;
  };

  const writeRange = (latencyForIndex: (index: number) => Pick<DayRow, "minutes" | "seconds">) => {
    const dates = eachUtcDayKey(bulkFrom, bulkTo);

    if (openEnded) {
      const existing = new Map(rows.map(row => [ row.date, row ]));

      dates.forEach((date, index) => {
        existing.set(date, {
          date,
          ...latencyForIndex(index)
        });
      });
      setRows(Array.from(existing.values()).sort((left, right) => left.date.localeCompare(right.date)));

      return;
    }

    const latencyByDate = new Map(dates.map((date, index) => [ date, latencyForIndex(index) ]));

    setRows(current => current.map(row => {
      const latency = latencyByDate.get(row.date);

      if (!latency) {
        return row;
      }

      return {
        ...row,
        ...latency
      };
    }));
  };

  const applyBulk = () => {
    if (!rangeReady()) {
      return;
    }

    writeRange(() => ({
      minutes: bulkMinutes,
      seconds: bulkSeconds
    }));
  };

  const applyIncrement = () => {
    if (!rangeReady()) {
      return;
    }

    if (!Number.isInteger(incrementSeconds)) {
      toast({
        status: "error",
        title: "Enter a whole number of seconds to add each day"
      });

      return;
    }

    const start = (bulkMinutes * 60) + bulkSeconds;

    writeRange(index => splitSeconds(start + (index * incrementSeconds)));
  };

  const save = () => {
    if (!startDate) {
      return;
    }

    const invalid = rows.some(row => row.minutes < 0 || row.seconds < 0 || row.seconds > 59);

    if (invalid) {
      toast({
        status: "error",
        title: "Each day needs minutes of 0 or more, and seconds from 0 to 59"
      });

      return;
    }

    const payload = {
      name: name.trim(),
      startDate: pickerDateToUtcKey(startDate),
      endDate: endDate ? pickerDateToUtcKey(endDate) : null,
      days: rows.map(row => ({
        date: row.date,
        latencySeconds: rowSeconds(row)
      }))
    };

    if (isEdit && missionId) {
      updateReq({
        id: missionId,
        ...payload
      });

      return;
    }

    createReq(payload);
  };

  const dayOptions = useMemo(() => rows.map(row => row.date), [ rows ]);

  const updateRow = useCallback((date: string, patch: Partial<DayRow>) => {
    setRows(current => current.map(row => row.date === date ? {
      ...row,
      ...patch
    } : row));
  }, []);

  return (
    <Box
      p={10}
      pb={20}
    >
      <Header />

      <Box paddingY={10}>
        <Text as="h1">
          {isEdit ? "Edit mission" : "Create mission"}
        </Text>

        <Text as="p">
          {step === 1 ? "Name the mission and choose its dates." : "Set the latency for each day. Unset days use 0m 10s."}
        </Text>
      </Box>

      {step === 1 && (
        <Box width="50%">
          <FormControl
            paddingY={5}
            id="mission-name"
          >
            <FormLabel>
              Name
            </FormLabel>

            <Input
              value={name}
              onChange={event => setName(event.target.value)}
            />
          </FormControl>

          <FormControl
            paddingY={5}
            id="mission-start"
          >
            <FormLabel>
              Start date
            </FormLabel>

            <DatePicker
              value={startDate}
              onChange={date => setStartDate(date)}
              format="DD-MM-YYYY"
              clearIcon={false}
            />
          </FormControl>

          <FormControl
            paddingY={5}
            id="mission-end"
          >
            <FormLabel>
              End date
            </FormLabel>

            <DatePicker
              value={endDate}
              onChange={date => setEndDate(date)}
              format="DD-MM-YYYY"
              allowClear={isGeneral}
              clearIcon={isGeneral ? undefined : false}
            />

            {isGeneral && (
              <Box marginTop="0.5rem">
                <Text as="small">
                  Leave this empty to keep GENERAL open-ended. Days without an entry stay at 0m 10s.
                </Text>
              </Box>
            )}
          </FormControl>

          <SolidButton
            marginTop="1rem"
            onClick={goToDays}
          >
            Next
          </SolidButton>
        </Box>
      )}

      {step === 2 && (
        <Box>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="flex-end"
            paddingY={5}
            gridGap={4}
          >
            <FormControl
              id="bulk-from"
              maxWidth="220px"
            >
              <FormLabel>
                From day
              </FormLabel>

              {openEnded ? (
                <DatePicker
                  value={bulkFrom ? utcKeyToPickerDate(bulkFrom) : null}
                  onChange={date => date && setBulkFrom(pickerDateToUtcKey(date))}
                  format="DD-MM-YYYY"
                  clearIcon={false}
                />
              ) : (
                <Input
                  as="select"
                  value={bulkFrom}
                  onChange={event => setBulkFrom(event.target.value)}
                >
                  <option value="">
                    Select
                  </option>

                  {dayOptions.map(date => (
                    <option
                      key={date}
                      value={date}
                    >
                      {`Day ${missionDayNumber(startKey, date)} · ${formatUtcDate(date)}`}
                    </option>
                  ))}
                </Input>
              )}
            </FormControl>

            <FormControl
              id="bulk-to"
              maxWidth="220px"
            >
              <FormLabel>
                To day
              </FormLabel>

              {openEnded ? (
                <DatePicker
                  value={bulkTo ? utcKeyToPickerDate(bulkTo) : null}
                  onChange={date => date && setBulkTo(pickerDateToUtcKey(date))}
                  format="DD-MM-YYYY"
                  clearIcon={false}
                />
              ) : (
                <Input
                  as="select"
                  value={bulkTo}
                  onChange={event => setBulkTo(event.target.value)}
                >
                  <option value="">
                    Select
                  </option>

                  {dayOptions.map(date => (
                    <option
                      key={date}
                      value={date}
                    >
                      {`Day ${missionDayNumber(startKey, date)} · ${formatUtcDate(date)}`}
                    </option>
                  ))}
                </Input>
              )}
            </FormControl>

            <FormControl
              id="bulk-minutes"
              maxWidth="120px"
            >
              <FormLabel>
                Minutes
              </FormLabel>

              <Input
                type="number"
                min={0}
                value={bulkMinutes}
                onChange={event => setBulkMinutes(Number(event.target.value))}
              />
            </FormControl>

            <FormControl
              id="bulk-seconds"
              maxWidth="120px"
            >
              <FormLabel>
                Seconds
              </FormLabel>

              <Input
                type="number"
                min={0}
                max={59}
                value={bulkSeconds}
                onChange={event => setBulkSeconds(Number(event.target.value))}
              />
            </FormControl>

            <SolidButton onClick={applyBulk}>
              Apply to range
            </SolidButton>
          </Box>

          <Box
            display="flex"
            flexDirection="row"
            alignItems="flex-end"
            paddingBottom={5}
            gridGap={4}
          >
            <FormControl
              id="bulk-increment"
              maxWidth="220px"
            >
              <FormLabel>
                Seconds added each day
              </FormLabel>

              <Input
                type="number"
                step={1}
                value={incrementSeconds}
                onChange={event => setIncrementSeconds(Number(event.target.value))}
              />
            </FormControl>

            <SolidButton onClick={applyIncrement}>
              Apply increment
            </SolidButton>
          </Box>

          <Box paddingBottom={4}>
            <Text as="small">
              The first day in the range uses the minutes and seconds above. Each following day adds the seconds shown here. Extra seconds roll into minutes.
            </Text>
          </Box>

          <Box
            maxHeight="50vh"
            overflowY="auto"
            border={`1px solid ${theme.colors.lightGrey}`}
          >
            <Box
              as="table"
              width="100%"
              sx={{
                "th, td": {
                  textAlign: "left",
                  padding: "8px"
                }
              }}
            >
              <Box as="thead">
                <Box as="tr">
                  <Box as="th">
                    Day
                  </Box>

                  <Box as="th">
                    Date
                  </Box>

                  <Box as="th">
                    Minutes
                  </Box>

                  <Box as="th">
                    Seconds
                  </Box>

                  {openEnded && (
                    <Box as="th">
                      Actions
                    </Box>
                  )}
                </Box>
              </Box>

              <Box as="tbody">
                {rows.map(row => (
                  <Box
                    as="tr"
                    key={row.date}
                  >
                    <Box as="td">
                      {missionDayNumber(startKey, row.date)}
                    </Box>

                    <Box as="td">
                      {formatUtcDate(row.date)}
                    </Box>

                    <Box as="td">
                      <Input
                        type="number"
                        min={0}
                        width="100px"
                        value={row.minutes}
                        onChange={event => updateRow(row.date, { minutes: Number(event.target.value) })}
                      />
                    </Box>

                    <Box as="td">
                      <Input
                        type="number"
                        min={0}
                        max={59}
                        width="100px"
                        value={row.seconds}
                        onChange={event => updateRow(row.date, { seconds: Number(event.target.value) })}
                      />
                    </Box>

                    {openEnded && (
                      <Box as="td">
                        <SolidButton
                          onClick={() => setRows(current => current.filter(item => item.date !== row.date))}
                        >
                          Use default
                        </SolidButton>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {openEnded && rows.length === 0 && (
            <Box paddingY={4}>
              <Text as="p">
                No overrides yet. Every day uses 0m 10s until you apply a range.
              </Text>
            </Box>
          )}

          <Box
            display="flex"
            paddingY={5}
            gridGap={4}
          >
            <SolidButton onClick={() => setStep(1)}>
              Back
            </SolidButton>

            <SolidButton
              onClick={save}
              isLoading={createRes.pending || updateRes.pending}
            >
              Save mission
            </SolidButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};
